/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  Button,
  Container,
  Heading,
  Progress,
  FontAwesomeIcon,
} from "components";

import classNames from "classnames";

import PageWrapper from "./PageWrapper";
import usePreviousRoute from "utils/usePreviousRoute";
import PageNote from "./PageNote";
import { FC, ReactNode } from "react";

type Props = {
  title: string;
  children: React.ReactNode;
  progressBarValues?: [];
  headerClassName?: string;
  containerClassName?: string;
  backgroundImage?: string;
  hasGoBack?: boolean;
  goBackMethod?: Function;
  hasPolicy?: boolean;
  hasProgressBar?: boolean;
  showFooter?: boolean;
  note?: string;
  headingClassName?: string;
  headingButtons?: ReactNode;
};

const PageLayout: FC<Props> = ({
  children,
  title,
  note,
  progressBarValues,
  containerClassName = "",
  hasGoBack,
  goBackMethod,
  hasPolicy,
  showFooter = true,
  hasProgressBar,
  headingClassName,
  headingButtons,
}) => {
  const goBack = usePreviousRoute("/");
  return (
    <PageWrapper showFooter={showFooter}>
      <Container
        className={classNames("px-[25px] md:px-0", containerClassName)}
      >
        {hasProgressBar && progressBarValues ? (
          <div className="hidden md:flex gap-x-1 mb-5">
            {progressBarValues.map((value, index) => (
              <Progress key={index} value={value} />
            ))}
          </div>
        ) : null}
        <div className="flex items-center justify-between w-full mb-[10px]">
          <div
            className={classNames(
              `flex items-center gap-x-4`,
              { "md:-ms-8": hasGoBack },
              headingClassName,
            )}
          >
            {hasGoBack && (
              <div>
                <Button
                  onClick={goBackMethod ? goBackMethod : goBack}
                  variant="ghost-unstyled"
                  className="!min-h-min -text--theme-colors-icon-neutral"
                >
                  <FontAwesomeIcon name="fa-chevron-left" size="xl" />
                </Button>
              </div>
            )}

            <Heading className="-text--theme-colors-text-title" as="h2">
              {title}
            </Heading>
          </div>
          {headingButtons && headingButtons}
        </div>
        <PageNote
          className={classNames("text-font-secondary max-w-4xl")}
          note={note}
          hasPolicy={hasPolicy}
        />
        {children}
      </Container>
    </PageWrapper>
  );
};

export default PageLayout;
